import { Accordion, AccordionItem, AccordionTrigger } from '@radix-ui/react-accordion';
import { useState } from 'react';
import { ToolCall } from './ToolCall';
import { AccordionContent } from '~/components/ui/Accordion';
import { Button } from '~/components/ui/Button';
import { SlidingNumber } from '~/components/ui/SlidingNumber';
import { TextShimmer } from '~/components/ui/TextShimmer';
import type { ToolInvocationGroupUIPart } from '~/lib/ai';
import { classNames } from '~/utils/classNames';

interface ToolCallGroupProps {
  part: ToolInvocationGroupUIPart & { id: string };
  isPending: boolean;
}

export function ToolCallGroup({ part, isPending }: ToolCallGroupProps) {
  const [accordionValue, setAccordionValue] = useState<string>('');

  let title = `actions taken`;
  let icon = 'i-heroicons-outline:ellipsis-horizontal-circle';

  const actions = new Set(
    part.toolInvocations.map(
      (toolInvocation) =>
        ({
          MultiEdit: 'Edit',
        })[toolInvocation.toolName] || toolInvocation.toolName,
    ),
  );

  if (actions.size > 1) {
    title = `actions taken`;
  } else if (actions.has('Read')) {
    title = `files read`;
    icon = 'i-heroicons-outline:eye';
  } else if (actions.has('Edit')) {
    title = `file edits`;
    icon = 'i-heroicons-outline:pencil-alt';
  } else if (actions.has('Bash')) {
    title = `commands executed`;
    icon = 'i-heroicons-outline:terminal';
  }

  return (
    <Accordion collapsible type="single" value={accordionValue} onValueChange={setAccordionValue}>
      <AccordionItem value="tool-invocations">
        <AccordionTrigger asChild className="group">
          <Button
            variant="ghost"
            truncate={false}
            size="md"
            className="flex justify-between w-full items-center gap-2 hover:bg-transparent group/button px-0 min-h-10"
          >
            {isPending && (
              <>
                <div className="grid [&>*]:[grid-area:1/1/2/2] justify-items-center items-center">
                  <span
                    className={classNames(
                      'i-bolt:circle-notch animate-spin',
                      'icon text-bolt-elements-icon-secondary pointer-events-none size-4.5 shrink-0 transition-transform duration-200 group-hover/button:text-bolt-ds-textPrimary',
                    )}
                  />
                  <span
                    className={classNames(
                      'i-heroicons-outline:ellipsis-horizontal  size-2.5',
                      'icon text-bolt-elements-icon-secondary pointer-events-none shrink-0 transition-transform duration-200 group-hover/button:text-bolt-ds-textPrimary',
                    )}
                  />
                </div>

                <div>
                  <SlidingNumber value={part.toolInvocations.length} />{' '}
                  <TextShimmer
                    className="[--base-color:var(--bolt-elements-textTertiary)] dark:[--base-color:var(--bolt-elements-textSecondary)] [--base-gradient-color:var(--bolt-elements-textPrimary)]"
                    duration={1.5}
                    spread={2}
                  >
                    {title}
                  </TextShimmer>
                </div>
              </>
            )}

            {!isPending && (
              <>
                <span
                  className={classNames(
                    icon,
                    'icon text-bolt-elements-icon-secondary size-4.5 pointer-events-none shrink-0 transition-transform duration-200 group-hover/button:text-bolt-ds-textPrimary',
                  )}
                />
                <div>
                  <SlidingNumber value={part.toolInvocations.length} />{' '}
                  <span className="text-sm text-bolt-ds-textSecondary font-normal group-hover/button:text-bolt-ds-textPrimary">
                    {title}
                  </span>
                </div>
              </>
            )}

            <span className="flex-1" />

            <span className="icon i-ph:caret-down-bold group-[[data-state=open]]:rotate-180 pointer-events-none size-4 shrink-0 transition-transform duration-200" />
          </Button>
        </AccordionTrigger>

        <AccordionContent className="pt-1">
          {part.toolInvocations.map((toolInvocation) => (
            <ToolCall key={toolInvocation.toolCallId} toolInvocation={toolInvocation} />
          ))}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
